import { useMutation, type PureQueryOptions } from '@apollo/client';
import type { ReactNode } from 'react';
import { toast } from 'react-toastify';
import { graphql } from '~/apollo/generated/v4';
import { Confirm } from '~/components/common/Confirm';

const DELETE_MINI_MODEL = graphql(`
  mutation DeleteMiniModel($id: ID!) {
    miniModelDelete(id: $id) {
      result {
        id
      }
    }
  }
`);

const DELETE_PHOTO_360 = graphql(`
  mutation DeletePhoto360($id: ID!) {
    photo360Delete(id: $id) {
      result {
        id
      }
    }
  }
`);

const DELETE_VIDEO = graphql(`
  mutation DeleteVideo($id: ID!) {
    videoDelete(id: $id) {
      result {
        id
      }
    }
  }
`);

export function DeleteUrlBasedSO({
  children,
  soType,
  id,
  isPlaced,
  refetchQueries,
}: {
  children: (confirmDelete: () => void, loadingDelete: boolean) => ReactNode;
  soType: 'MiniModel' | 'Photo360' | 'Video';
  id: number;
  isPlaced: boolean;
  refetchQueries?: PureQueryOptions[];
}) {
  const [deleteMiniModel, { loading: loadingMM }] = useMutation(
    DELETE_MINI_MODEL,
    { variables: { id }, refetchQueries },
  );
  const [deletePhoto360, { loading: loadingP360 }] = useMutation(
    DELETE_PHOTO_360,
    { variables: { id }, refetchQueries },
  );
  const [deleteVideo, { loading: loadingVideo }] = useMutation(DELETE_VIDEO, {
    variables: { id },
    refetchQueries,
  });

  async function handleDelete() {
    const handleError = (err: unknown) => {
      console.log(`Error deleting ${soType} ${id}`, err);
      toast.error(
        'There was a problem deleting the item, please reload the page and try again.',
      );
    };

    switch (soType) {
      case 'MiniModel':
        await deleteMiniModel().catch(handleError);
        break;
      case 'Photo360':
        await deletePhoto360().catch(handleError);
        break;
      case 'Video':
        await deleteVideo().catch(handleError);
        break;
      default:
        soType satisfies never;
    }

    toast.success('Item deleted successfully.');
  }

  const loading = loadingMM || loadingP360 || loadingVideo;

  return (
    <Confirm
      onConfirm={handleDelete}
      text={
        <div className="space-y-2">
          <p>Are you sure you want to delete this item?</p>
          {isPlaced && (
            <p className="text-error font-bold">
              This item has already been placed!
            </p>
          )}
        </div>
      }
      submitButtonColor={isPlaced ? 'error' : 'primary'}
    >
      {confirmDelete => children(confirmDelete, loading)}
    </Confirm>
  );
}
